import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, BadgeCheck, Briefcase, Check, ChevronDown, Gem,
  Minus, PartyPopper, Plus, Send, Users,
} from "lucide-react";
import { FadeUp, Eyebrow } from "../components/Reveal";
import { HalalBadge } from "../components/Navbar";
import { InquiryPhotos } from "../components/InquiryPhotos";
import { CateringOccasions, INQUIRY_ANCHOR, openOccasion, onSelectStyle } from "../components/CateringOccasions";
import { api, formatApiError, imgSrc } from "../lib/api";
import { MEALS, EXTRAS, DELIVERY_NOTICE, fmt } from "../data/site";
import { useSitePhotos } from "../lib/sitePhotos";
import { useSEO } from "../lib/useSEO";

const PACKAGE_ICONS = { briefcase: Briefcase, party: PartyPopper, gem: Gem };
const PACKAGE_OCCASION = { boardroom: "corporate", gathering: "parties", celebration: "wedding" };
const PACKAGE_ALT = {
  boardroom: "Corporate halal lunch buffet in a boardroom",
  gathering: "Halal party buffet with saffron rice, kofta, sweet plantains, mezze and cake.",
};
const GALLERY_ALT = [
  "Halal catering buffet spread with grilled chicken, falafel and salads",
  "Baklava and pistachio cake dessert table",
  "Mezze spread with kofta, hummus and fattoush",
  "Halal chicken kabsa catering platter",
];

const FAQS = [
  {
    q: "Is everything really 100% halal?",
    a: "Yes. Every protein is sourced from certified halal suppliers, and our Woodbridge kitchen is fully halal — no alcohol, no cross-contamination, no exceptions.",
  },
  {
    q: "How far in advance should I book catering?",
    a: "Corporate drop-offs need 72 hours. Parties and grazing tables need one week. Weddings and full-service events are best booked 4–8 weeks out so we can schedule your tasting.",
  },
  {
    q: "Do you deliver and set up, or just drop off?",
    a: "Both. The Boardroom is a polished drop-off with chafing setup; The Gathering and The Celebration include service staff, stations, and full breakdown.",
  },
  {
    q: "Where do you cater?",
    a: "Anywhere in the DMV — Washington DC, Maryland, and Northern Virginia. Delivery only; we do not offer pickup from our Woodbridge kitchen.",
  },
];

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3" data-testid="catering-faq">
      {FAQS.map((f, i) => (
        <div key={f.q} className="overflow-hidden rounded-2xl border border-inkline bg-parchment">
          <button
            data-testid={`faq-q-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between gap-4 p-5 text-left"
          >
            <span className="font-display text-base font-semibold text-ink">{f.q}</span>
            <ChevronDown size={18} className={`shrink-0 text-spice transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <p className="px-5 pb-5 text-sm leading-relaxed text-muted2" data-testid={`faq-a-${i}`}>{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const Estimator = ({ packages, onEstimate }) => {
  const [guests, setGuests] = useState(50);
  const [style, setStyle] = useState(packages[0]?.id);
  const [dessert, setDessert] = useState(false);
  const [drinks, setDrinks] = useState(false);

  useEffect(() => onSelectStyle((id) => {
    if (packages.some((p) => p.id === id)) {
      setStyle(id);
      update(guests, id, dessert, drinks);
    }
  }));

  const pkg = packages.find((p) => p.id === style) || packages[0];
  const estimate = useMemo(() => {
    let per = pkg?.price || 0;
    if (dessert) per += 4;
    if (drinks) per += 3;
    return per * guests;
  }, [pkg, guests, dessert, drinks]);

  const update = (g, s, d, dr) => {
    const p = packages.find((x) => x.id === s) || packages[0];
    let per = p?.price || 0;
    if (d) per += 4;
    if (dr) per += 3;
    onEstimate(per * g);
  };

  return (
    <div className="rounded-3xl bg-parchment p-7 text-ink shadow-2xl shadow-ink/30 sm:p-9" data-testid="quote-estimator">
      <Eyebrow>Instant estimate</Eyebrow>
      <h3 className="mt-2 font-display text-2xl font-semibold">Ballpark your event.</h3>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-ink/80">Guests</label>
          <div className="flex items-center gap-3">
            <button data-testid="guests-minus" onClick={() => { const g = Math.max(10, guests - 10); setGuests(g); update(g, style, dessert, drinks); }} className="flex h-8 w-8 items-center justify-center rounded-full bg-cream text-ink transition-colors hover:bg-spice hover:text-parchment" aria-label="Fewer guests"><Minus size={14} /></button>
            <span className="w-12 text-center font-mono text-lg font-bold" data-testid="guests-count">{guests}</span>
            <button data-testid="guests-plus" onClick={() => { const g = Math.min(500, guests + 10); setGuests(g); update(g, style, dessert, drinks); }} className="flex h-8 w-8 items-center justify-center rounded-full bg-cream text-ink transition-colors hover:bg-spice hover:text-parchment" aria-label="More guests"><Plus size={14} /></button>
          </div>
        </div>
        <input
          type="range" min={10} max={500} step={10} value={guests}
          data-testid="guests-slider"
          onChange={(e) => { const g = Number(e.target.value); setGuests(g); update(g, style, dessert, drinks); }}
          className="mt-3 w-full accent-[#D94A26]"
        />
      </div>

      <div className="mt-6 grid grid-cols-3 gap-2">
        {packages.map((p) => (
          <button
            key={p.id}
            data-testid={`style-${p.id}`}
            onClick={() => { setStyle(p.id); update(guests, p.id, dessert, drinks); }}
            className={`rounded-xl border-2 px-2 py-3 text-center transition-all duration-300 ${
              (pkg?.id === p.id) ? "border-spice bg-spice/5" : "border-inkline hover:border-spice/40"
            }`}
          >
            <span className="block text-xs font-bold">{p.name.replace("The ", "")}</span>
            <span className="block font-mono text-[10px] text-muted2">{fmt(p.price)}/pp</span>
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-2.5">
        {[
          { label: "Dessert station (+$4/pp)", value: dessert, set: setDessert, testid: "addon-dessert" },
          { label: "Drinks & mint lemonade (+$3/pp)", value: drinks, set: setDrinks, testid: "addon-drinks" },
        ].map((a) => (
          <button
            key={a.testid}
            data-testid={a.testid}
            onClick={() => { const v = !a.value; a.set(v); update(guests, style, a.testid === "addon-dessert" ? v : dessert, a.testid === "addon-drinks" ? v : drinks); }}
            className="flex w-full items-center justify-between rounded-xl border border-inkline px-4 py-3 text-sm font-medium transition-colors hover:border-spice/40"
          >
            {a.label}
            <span className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors ${a.value ? "border-spice bg-spice text-parchment" : "border-inkline"}`}>
              {a.value && <Check size={12} strokeWidth={3} />}
            </span>
          </button>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-ink p-5 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-saffron">Estimated total</div>
        <div className="mt-1 font-display text-4xl font-bold text-parchment" data-testid="estimate-total">{fmt(estimate)}</div>
        <div className="mt-1 text-xs text-parchment/60">{guests} guests · {pkg.name}</div>
      </div>
      <p className="mt-3 text-center text-[11px] text-muted2">Final quote confirmed after a quick call with our catering team.</p>
    </div>
  );
};

const InquiryForm = ({ packages, estimate }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_type: "Corporate", event_date: "",
    guests: 50, location: "", service_style: packages[0]?.name || "", message: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => onSelectStyle((id) => {
    const pkg = packages.find((p) => p.id === id);
    if (pkg) setForm((cur) => ({ ...cur, service_style: pkg.name }));
  }), [packages]);
  const inputCls = "w-full rounded-xl border border-inkline bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-muted2/60 focus:border-spice";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/catering-inquiries", { ...form, guests: Number(form.guests), estimate, attachments });
      toast.success("Inquiry received — our catering team will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", event_type: "Corporate", event_date: "", guests: 50, location: "", service_style: packages[0]?.name || "", message: "" });
      setAttachments([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-3xl border border-inkline bg-parchment p-7 sm:p-9" data-testid="catering-inquiry-form">
      <div>
        <Eyebrow>Catering inquiry</Eyebrow>
        <h3 className="mt-2 font-display text-2xl font-semibold text-ink">Tell us about your event.</h3>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input required data-testid="inquiry-name" placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
        <input required type="email" data-testid="inquiry-email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
        <input required data-testid="inquiry-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
        <select data-testid="inquiry-event-type" value={form.event_type} onChange={set("event_type")} className={inputCls}>
          <option>Corporate</option>
          <option>Wedding</option>
          <option>Private Party</option>
          <option>Other</option>
        </select>
        <input required type="date" data-testid="inquiry-date" value={form.event_date} onChange={set("event_date")} className={inputCls} />
        <input required type="number" min={10} data-testid="inquiry-guests" placeholder="Guest count" value={form.guests} onChange={set("guests")} className={inputCls} />
      </div>
      <input required data-testid="inquiry-location" placeholder="Event location (city / venue in the DMV)" value={form.location} onChange={set("location")} className={inputCls} />
      <select data-testid="inquiry-service-style" value={form.service_style} onChange={set("service_style")} className={inputCls}>
        {packages.map((p) => (
          <option key={p.id}>{p.name}</option>
        ))}
      </select>
      <textarea data-testid="inquiry-message" rows={4} placeholder="Menu wishes, dietary notes, timeline…" value={form.message} onChange={set("message")} className={inputCls} />
      <InquiryPhotos value={attachments} onChange={setAttachments} />
      {estimate > 0 && (
        <p className="rounded-xl bg-saffron/10 p-3.5 text-xs font-medium text-ink/80" data-testid="inquiry-estimate-note">
          Your instant estimate of {fmt(estimate)} will be attached to this inquiry.
        </p>
      )}
      <p className="text-xs text-muted2">{DELIVERY_NOTICE}</p>
      <button
        type="submit"
        data-testid="inquiry-submit-btn"
        disabled={sending}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-spice py-4 text-sm font-bold text-parchment transition-colors duration-300 hover:bg-ink disabled:opacity-60"
      >
        <Send size={15} /> {sending ? "Sending…" : "Send Catering Inquiry"}
      </button>
    </form>
  );
};

export default function Catering() {
  useSEO({ title: "Halal Catering in the DMV — Corporate, Parties & Weddings | HiPlate", description: "Full-service 100% halal catering from Woodbridge, VA: corporate lunches, grazing tables, plated wedding menus. Instant per-guest estimates and inquiries.", path: "/catering" });
  const [estimate, setEstimate] = useState(1600);
  const [packages, setPackages] = useState(null);
  const [favorites, setFavorites] = useState(MEALS.slice(0, 7));
  const [extras, setExtras] = useState(EXTRAS);
  const IMG = useSitePhotos();

  useEffect(() => {
    api.get("/catering-packages")
      .then(({ data }) => setPackages(data))
      .catch(() => setPackages([]));
    api.get("/menu")
      .then(({ data }) => { if (data.length) setFavorites(data.slice(0, 7)); })
      .catch(() => {});
    api.get("/extras")
      .then(({ data }) => { if (Array.isArray(data)) setExtras(data); })
      .catch(() => {});
  }, []);

  return (
    <main className="bg-parchment">
      <section className="grain relative overflow-hidden bg-ink pb-20 pt-40 text-parchment lg:pt-48" data-testid="catering-hero">
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-2">
          <FadeUp>
            <HalalBadge />
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Events that taste like <span className="italic text-saffron">occasions.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-parchment/70">
              Corporate lunches, milestone parties, and once-in-a-lifetime weddings — fully halal,
              fully serviced, delivered and staffed anywhere in the DMV.
            </p>
            <CateringOccasions />
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-saffron/20" aria-hidden="true" />
              <img src={IMG.feastTable} alt="Halal catering buffet spread with grilled chicken, falafel, hummus and salads" className="relative h-80 w-full rounded-3xl object-cover shadow-2xl sm:h-96" data-testid="catering-hero-image" />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28" data-testid="catering-packages">
        <FadeUp>
          <Eyebrow>Three ways we cater</Eyebrow>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Pick a service style. We'll handle everything after.
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {(packages || []).map((p, i) => {
            const Icon = PACKAGE_ICONS[p.icon] || PartyPopper;
            return (
              <FadeUp key={p.id} delay={i * 0.1}>
                <button
                  type="button"
                  onClick={() => openOccasion(PACKAGE_OCCASION[p.id] || "parties", p.id, imgSrc(p.img))}
                  aria-label={`${p.name} — view details and request a quote`}
                  className="group flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-3xl border border-inkline bg-cream text-left transition-all duration-500 hover:-translate-y-1.5 hover:border-spice/40 hover:shadow-2xl hover:shadow-ink/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-spice"
                  data-testid={`package-${p.id}`}
                >
                  <div className="relative overflow-hidden">
                    <img src={imgSrc(p.img)} alt={PACKAGE_ALT[p.id] || `${p.name} — ${p.subtitle}`} loading="lazy" className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-parchment/90 text-spice backdrop-blur-sm">
                      <Icon size={20} />
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="font-display text-2xl font-semibold text-ink">{p.name}</h3>
                      <span className="shrink-0 font-mono text-sm font-bold text-spice">{fmt(p.price)}/pp</span>
                    </div>
                    <p className="mt-1 text-sm text-muted2">{p.subtitle}</p>
                    <ul className="mt-5 flex-1 space-y-2.5">
                      {p.includes.map((inc) => (
                        <li key={inc} className="flex items-start gap-2.5 text-sm text-ink/80">
                          <BadgeCheck size={15} className="mt-0.5 shrink-0 text-halal" /> {inc}
                        </li>
                      ))}
                    </ul>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-spice transition-colors group-hover:text-ink" data-testid={`package-cta-${p.id}`}>
                      Details &amp; get a quote <ArrowRight size={15} />
                    </span>
                  </div>
                </button>
              </FadeUp>
            );
          })}
        </div>
      </section>

      <section id={INQUIRY_ANCHOR} className="grain scroll-mt-24 bg-moss py-20 lg:py-28" data-testid="catering-quote-section">
        <div className="relative z-10 mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-8 lg:grid-cols-2">
          <FadeUp>
            {packages && packages.length > 0 && <Estimator packages={packages} onEstimate={setEstimate} />}
          </FadeUp>
          <FadeUp delay={0.12}>
            {packages && <InquiryForm packages={packages} estimate={estimate} />}
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28" data-testid="catering-gallery">
        <FadeUp>
          <Eyebrow>Recent tables</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">A glimpse of the spread.</h2>
        </FadeUp>
        <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[IMG.gallery1, IMG.gallery2, IMG.gallery3, IMG.gallery4].map((src, i) => (
            <FadeUp key={i} delay={i * 0.08} className={i % 2 === 1 ? "lg:mt-10" : ""}>
              <div className="overflow-hidden rounded-2xl">
                <img src={src} alt={GALLERY_ALT[i]} loading="lazy" className="h-56 w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-72" data-testid={`gallery-img-${i}`} />
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-14">
          <div className="rounded-3xl border border-inkline bg-cream p-7 sm:p-9">
            <h3 className="font-display text-xl font-semibold text-ink">Catering menu favorites</h3>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {favorites.map((m) => (
                <span key={m.id} className="rounded-full border border-inkline bg-parchment px-4 py-2 text-xs font-medium text-ink/80">{m.name}</span>
              ))}
              {extras.map((e) => (
                <span key={e.id} className="rounded-full border border-saffron/40 bg-saffron/10 px-4 py-2 text-xs font-medium text-ink/80">{e.name}</span>
              ))}
            </div>
          </div>
        </FadeUp>
      </section>

      <section className="bg-cream py-20 lg:py-24" data-testid="catering-faq-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-8 lg:grid-cols-5">
          <FadeUp className="lg:col-span-2">
            <Eyebrow>Good to know</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">Catering questions, answered.</h2>
            <p className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-muted2">
              <Users size={16} className="mt-0.5 shrink-0 text-spice" />
              We cater groups from 10 to 500. Every event gets a dedicated coordinator and a
              confirmed halal menu before the big day.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-3">
            <Faq />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
